/**
 * AnglesLens — full-featured angle management tab.
 *
 * Layout:
 *  - Header with approve count, "Suggest More" and "Generate Assets" actions
 *  - Active (approved) angles: full expandable cards with rich detail
 *    - Scenario Context panel (fetched on expand, cached by scenario_id)
 *    - Generated Assets strip (all brief-level packs shown inline)
 *  - Suggested angles: greyed-out cards with dashed border, quick approve/reject
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Image as ImageIcon,
  Layers,
  Lightbulb,
  Loader2,
  MessageSquare,
  Package,
  Plus,
  Sparkles,
  Target,
  TrendingUp,
  X,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';
import {
  generationApi,
  marketResearchApi,
  type AngleStatus,
  type BriefAngle,
  type GenerationBrief,
  type AdPackStatusResponse,
  type ScenarioDetail,
  getApiBase,
} from '@/services/api';
import { useGenerationStore } from '@/stores/generation';
import { AssetDetailModal, type ModalAsset } from '@/components/AssetDetailModal';
import type { UseBriefWorkspaceChatReturn } from '../useBriefWorkspaceChat';

const MIN_SUGGESTED = 3;
const AGENT_BASE_URL = getApiBase();

/* ─── Pack status helpers ────────────────────────────────────────────────── */

function isCompletedPackStatus(status?: string): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'completed' || s === 'success' || s === 'complete' || s === 'published' || s === 'partial';
}

function isActivePackStatus(status?: string): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return s === 'running' || s === 'pending' || s === 'queued' || s === 'initializing' || s === 'generating' || s === 'paused';
}

/* ─── Thumbnail hook (lazy per pack) ────────────────────────────────────── */

function useThumbnails(packId: string | null, enabled = true): {
  thumbnails: ModalAsset[];
  loading: boolean;
} {
  const [thumbnails, setThumbnails] = useState<ModalAsset[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!packId || !enabled) return;
    let cancelled = false;
    setLoading(true);
    setThumbnails([]);
    fetch(`${AGENT_BASE_URL}/api/v1/creative-canvas/asset_pack/${encodeURIComponent(packId)}/tracks/final_ad`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { assets?: ModalAsset[] } | null) => {
        if (!cancelled && data?.assets) setThumbnails(data.assets);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [packId, enabled]);

  return { thumbnails, loading };
}

/* ─── Scenario context cache hook ────────────────────────────────────────── */

const scenarioCache = new Map<string, ScenarioDetail | null>();

function useScenarioDetail(scenarioId: string | undefined, enabled = false) {
  const [detail, setDetail] = useState<ScenarioDetail | null>(
    scenarioId && scenarioCache.has(scenarioId) ? (scenarioCache.get(scenarioId) ?? null) : null,
  );
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef('');

  useEffect(() => {
    if (!scenarioId || !enabled) return;
    if (fetchedRef.current === scenarioId) return;
    if (scenarioCache.has(scenarioId)) {
      setDetail(scenarioCache.get(scenarioId) ?? null);
      return;
    }
    fetchedRef.current = scenarioId;
    setLoading(true);
    marketResearchApi
      .getScenario(scenarioId)
      .then((d) => {
        scenarioCache.set(scenarioId, d);
        setDetail(d);
      })
      .catch(() => {
        scenarioCache.set(scenarioId, null);
      })
      .finally(() => setLoading(false));
  }, [scenarioId, enabled]);

  return { detail, loading };
}

/* ─── Props ─────────────────────────────────────────────────────────────── */

interface Props {
  brief: GenerationBrief;
  chat: UseBriefWorkspaceChatReturn;
  onRequestGenerate: (angleId?: string) => void;
  onNavigateToAssets?: () => void;
  /** Called with the full updated angle list after any mutation. */
  onAnglesChanged?: (angles: BriefAngle[]) => void;
}

/* ─── AnglesLens ─────────────────────────────────────────────────────────── */

export function AnglesLens({
  brief,
  chat,
  onRequestGenerate,
  onNavigateToAssets,
  onAnglesChanged,
}: Props) {
  const angles = brief.selected_angles ?? [];
  const fetchBriefAdPackJobs = useGenerationStore((s) => s.fetchBriefAdPackJobs);
  const adPackJobs = useGenerationStore(
    (s) => s.briefAdPackJobsByBriefId[brief.brief_id] ?? [],
  );

  const [suggesting, setSuggesting] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef<string>('');

  const approved = angles.filter((a) => a.status === 'approved');
  const suggested = angles.filter((a) => a.status === 'suggested');
  const rejected = angles.filter((a) => a.status === 'rejected');

  const completedJobs = adPackJobs.filter((j) => isCompletedPackStatus(j.status));
  const activeJobs = adPackJobs.filter((j) => isActivePackStatus(j.status));

  // Fetch ad pack jobs for context
  useEffect(() => {
    void fetchBriefAdPackJobs(brief.brief_id);
  }, [brief.brief_id, fetchBriefAdPackJobs]);

  // Auto-fetch angles on mount if below threshold
  useEffect(() => {
    if (hasFetchedRef.current === brief.brief_id) return;
    const suggestedCount = angles.filter((a) => a.status === 'suggested').length;
    if (suggestedCount >= MIN_SUGGESTED) {
      hasFetchedRef.current = brief.brief_id;
      return;
    }
    hasFetchedRef.current = brief.brief_id;
    const needed = Math.max(MIN_SUGGESTED - suggestedCount, MIN_SUGGESTED);
    setAutoLoading(true);
    generationApi
      .suggestAngles(brief.brief_id, needed)
      .then((r) => {
        const existingIds = new Set(angles.map((a) => a.angle_id));
        const fresh = r.suggested_angles.filter((a) => !existingIds.has(a.angle_id));
        if (fresh.length > 0) onAnglesChanged?.([...fresh, ...angles]);
      })
      .catch(() => {})
      .finally(() => setAutoLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief.brief_id]);

  const updateAngle = useCallback(
    async (angle: BriefAngle, status: AngleStatus) => {
      try {
        const updated = await generationApi.updateAngle(brief.brief_id, angle.angle_id, status);
        onAnglesChanged?.(angles.map((a) => (a.angle_id === angle.angle_id ? updated : a)));
      } catch (e) {
        setError(String(e));
      }
    },
    [brief.brief_id, angles, onAnglesChanged],
  );

  const suggestMore = async () => {
    setError(null);
    setSuggesting(true);
    try {
      const r = await generationApi.suggestAngles(brief.brief_id, 5);
      const existingIds = new Set(angles.map((a) => a.angle_id));
      const fresh = r.suggested_angles.filter((a) => !existingIds.has(a.angle_id));
      onAnglesChanged?.([...fresh, ...angles]);
    } catch (e) {
      setError(String(e));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">Creative Angles</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {approved.length} approved · {suggested.length} suggested
            {rejected.length > 0 && ` · ${rejected.length} rejected`}
            {completedJobs.length > 0 && (
              <> · <span className="text-emerald-600 font-medium">{completedJobs.length} packs generated</span></>
            )}
            {activeJobs.length > 0 && (
              <> · <span className="text-amber-600 font-medium">{activeJobs.length} in progress</span></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={suggestMore}
            disabled={suggesting}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-brand-purple rounded-lg hover:bg-brand-purple/90 disabled:opacity-50 transition"
          >
            {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Suggest More
          </button>
          <button
            onClick={() => onRequestGenerate()}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg hover:from-emerald-600 hover:to-teal-600 transition"
          >
            <Zap size={12} /> Generate Assets
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {/* Approved angles */}
      {approved.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">
              Approved for Generation
            </span>
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
              {approved.length}
            </span>
          </div>
          <div className="space-y-2">
            {approved.map((angle) => (
              <AngleCard
                key={angle.angle_id}
                angle={angle}
                variant="approved"
                isPinned={chat.activeAngles?.includes(angle.angle_id) ?? false}
                adPackJobs={adPackJobs}
                briefId={brief.brief_id}
                onTogglePin={() => chat.toggleTarget('angle', angle.angle_id)}
                onApprove={() => updateAngle(angle, 'approved')}
                onReject={() => updateAngle(angle, 'rejected')}
                onSuggest={() => updateAngle(angle, 'suggested')}
                onGenerate={() => onRequestGenerate(angle.angle_id)}
                onNavigateToAssets={onNavigateToAssets}
              />
            ))}
          </div>
        </section>
      )}

      {/* Suggested angles */}
      {(suggested.length > 0 || autoLoading) && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
              Suggested
            </span>
            {suggested.length > 0 && (
              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold">
                {suggested.length}
              </span>
            )}
            {autoLoading && (
              <span className="flex items-center gap-1 text-[10px] text-slate-400">
                <Loader2 size={9} className="animate-spin" /> Generating scenario-grounded angles…
              </span>
            )}
          </div>
          <div className="space-y-2">
            {suggested.map((angle) => (
              <AngleCard
                key={angle.angle_id}
                angle={angle}
                variant="suggested"
                isPinned={chat.activeAngles?.includes(angle.angle_id) ?? false}
                adPackJobs={[]}
                briefId={brief.brief_id}
                onTogglePin={() => chat.toggleTarget('angle', angle.angle_id)}
                onApprove={() => updateAngle(angle, 'approved')}
                onReject={() => updateAngle(angle, 'rejected')}
                onSuggest={() => updateAngle(angle, 'suggested')}
                onGenerate={() => onRequestGenerate(angle.angle_id)}
              />
            ))}
            {autoLoading &&
              Array.from({ length: Math.max(MIN_SUGGESTED - suggested.length, 0) }).map((_, i) => (
                <AngleCardSkeleton key={`skel-${i}`} />
              ))}
          </div>
        </section>
      )}

      {/* Rejected (collapsed) */}
      {rejected.length > 0 && (
        <RejectedCollapse angles={rejected} onRestore={(angle) => updateAngle(angle, 'suggested')} />
      )}

      {/* Empty state */}
      {angles.length === 0 && !autoLoading && (
        <div className="text-center py-16">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <Lightbulb size={20} className="text-slate-300" />
          </div>
          <p className="text-sm font-medium text-slate-600">No angles yet</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">
            Generate AI-powered creative angles grounded in your persona's buyer scenarios.
          </p>
          <button
            onClick={suggestMore}
            disabled={suggesting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-brand-purple rounded-lg hover:bg-brand-purple/90 disabled:opacity-50"
          >
            {suggesting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Suggest Angles
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Skeleton card ─────────────────────────────────────────────────────── */

function AngleCardSkeleton() {
  return (
    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-4 py-3 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-5 h-5 rounded-full bg-slate-200 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <div className="h-4 w-24 bg-slate-200 rounded-full" />
            <div className="h-4 w-16 bg-slate-100 rounded-full" />
          </div>
          <div className="h-4 w-3/4 bg-slate-200 rounded" />
          <div className="h-3 w-1/2 bg-slate-100 rounded" />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <div className="h-6 w-12 bg-slate-200 rounded-lg" />
          <div className="h-6 w-10 bg-slate-100 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/* ─── Angle Card ─────────────────────────────────────────────────────────── */

function AngleCard({
  angle,
  variant,
  isPinned,
  adPackJobs,
  briefId,
  onTogglePin,
  onApprove,
  onReject,
  onSuggest,
  onGenerate,
  onNavigateToAssets,
}: {
  angle: BriefAngle;
  variant: 'approved' | 'suggested';
  isPinned: boolean;
  adPackJobs: AdPackStatusResponse[];
  briefId: string;
  onTogglePin: () => void;
  onApprove: () => void;
  onReject: () => void;
  onSuggest: () => void;
  onGenerate: () => void;
  onNavigateToAssets?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isSuggested = variant === 'suggested';

  const completedPacks = adPackJobs.filter((j) => isCompletedPackStatus(j.status));
  const activePacks = adPackJobs.filter((j) => isActivePackStatus(j.status));
  const totalPackCount = completedPacks.length + activePacks.length;

  return (
    <div
      className={clsx(
        'rounded-xl border transition overflow-hidden',
        isSuggested
          ? 'border-dashed border-slate-300 bg-slate-50/60 opacity-80'
          : 'border-slate-200 bg-white shadow-sm',
      )}
    >
      {/* Card header row */}
      <div className="flex items-start gap-3 px-4 py-3">
        {/* Status indicator / approve button */}
        <button
          onClick={isSuggested ? onApprove : onSuggest}
          title={isSuggested ? 'Approve this angle' : 'Move back to suggested'}
          className={clsx(
            'mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition',
            !isSuggested
              ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
              : 'border-slate-300 text-slate-300 hover:border-emerald-400 hover:text-emerald-400',
          )}
        >
          <Check size={11} />
        </button>

        <div className="flex-1 min-w-0">
          {/* Badges row */}
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            {angle.scenario_name && (
              <span className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 text-[10px] font-medium">
                <Target size={8} />
                {angle.scenario_name}
              </span>
            )}
            {angle.source === 'agentic' && (
              <span className="inline-flex items-center gap-1 bg-brand-purple/10 text-brand-purple rounded-full px-2 py-0.5 text-[10px] font-medium">
                <Sparkles size={8} /> AI
              </span>
            )}
            {!isSuggested && totalPackCount > 0 && (
              <button
                onClick={() => { setExpanded(true); }}
                className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 text-[10px] font-medium hover:bg-emerald-100 transition"
              >
                <Package size={8} />
                {completedPacks.length} pack{completedPacks.length !== 1 ? 's' : ''}
                {activePacks.length > 0 && <span className="text-amber-600"> · {activePacks.length} running</span>}
              </button>
            )}
          </div>

          {/* Hook */}
          <p className={clsx('text-sm font-semibold leading-snug', isSuggested ? 'text-slate-600' : 'text-slate-900')}>
            {angle.hook}
          </p>

          {/* Message preview */}
          {!expanded && angle.message && (
            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{angle.message}</p>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {!isSuggested && (
            <button
              onClick={onGenerate}
              title="Generate assets from this angle"
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition"
            >
              <Zap size={9} /> Generate
            </button>
          )}
          <button
            onClick={onTogglePin}
            title={isPinned ? 'Unpin from chat' : 'Pin to chat'}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded-lg border transition',
              isPinned
                ? 'bg-brand-purple text-white border-brand-purple'
                : 'bg-white text-slate-500 border-slate-200 hover:border-brand-purple hover:text-brand-purple',
            )}
          >
            <MessageSquare size={9} />
            {isPinned ? 'Pinned' : 'Pin'}
          </button>
          <button
            onClick={onReject}
            title="Reject this angle"
            className="w-6 h-6 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-400 transition"
          >
            <X size={11} />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-6 h-6 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:border-slate-300 transition"
          >
            <ChevronDown
              size={12}
              className={clsx('transition-transform', expanded && 'rotate-180')}
            />
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <AngleExpandedDetail
          angle={angle}
          isSuggested={isSuggested}
          adPackJobs={adPackJobs}
          briefId={briefId}
          onApprove={onApprove}
          onReject={onReject}
          onNavigateToAssets={onNavigateToAssets}
        />
      )}
    </div>
  );
}

/* ─── Expanded Detail ────────────────────────────────────────────────────── */

function AngleExpandedDetail({
  angle,
  isSuggested,
  adPackJobs,
  briefId,
  onApprove,
  onReject,
  onNavigateToAssets,
}: {
  angle: BriefAngle;
  isSuggested: boolean;
  adPackJobs: AdPackStatusResponse[];
  briefId: string;
  onApprove: () => void;
  onReject: () => void;
  onNavigateToAssets?: () => void;
}) {
  const { detail: scenarioDetail, loading: scenarioLoading } = useScenarioDetail(
    angle.scenario_id,
    true,
  );

  const completedPacks = adPackJobs.filter((j) => isCompletedPackStatus(j.status));
  const activePacks = adPackJobs.filter((j) => isActivePackStatus(j.status));

  return (
    <div className="border-t border-slate-100 bg-slate-50/50">
      {/* Angle detail fields */}
      <div className="px-4 py-4 space-y-3">
        {angle.message && (
          <DetailBlock icon={<MessageSquare size={12} />} label="Message">
            {angle.message}
          </DetailBlock>
        )}
        {angle.emotional_lever && (
          <DetailBlock icon={<Sparkles size={12} />} label="Emotional Lever">
            {angle.emotional_lever}
          </DetailBlock>
        )}
        {angle.strategic_rationale && (
          <DetailBlock icon={<Target size={12} />} label="Strategic Rationale">
            {angle.strategic_rationale}
          </DetailBlock>
        )}
        {angle.concept_ids && angle.concept_ids.length > 0 && (
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">
              PDO Concepts
            </span>
            <div className="flex flex-wrap gap-1.5">
              {angle.concept_ids.map((cid) => (
                <span
                  key={cid}
                  className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono"
                >
                  {cid}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1">
          <span>
            {angle.source === 'agentic'
              ? 'AI-generated'
              : angle.source === 'manual'
                ? 'Manually added'
                : 'From brief generation'}
          </span>
          {angle.created_at && (
            <span>Added {new Date(angle.created_at).toLocaleDateString()}</span>
          )}
        </div>

        {isSuggested && (
          <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
            <button
              onClick={onApprove}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition"
            >
              <Check size={11} /> Approve
            </button>
            <button
              onClick={onReject}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
            >
              <X size={11} /> Reject
            </button>
          </div>
        )}
      </div>

      {/* Scenario Context */}
      {angle.scenario_id && (
        <ScenarioContextPanel
          scenarioId={angle.scenario_id}
          scenarioName={angle.scenario_name}
          detail={scenarioDetail}
          loading={scenarioLoading}
        />
      )}

      {/* Generated Assets */}
      {!isSuggested && (completedPacks.length > 0 || activePacks.length > 0) && (
        <GeneratedAssetsPanel
          completedPacks={completedPacks}
          activePacks={activePacks}
          briefId={briefId}
          onNavigateToAssets={onNavigateToAssets}
        />
      )}

      {/* CTA to generate assets for this angle if none exist */}
      {!isSuggested && completedPacks.length === 0 && activePacks.length === 0 && (
        <div className="mx-4 mb-4 px-4 py-3 rounded-xl border border-dashed border-emerald-200 bg-emerald-50/50 flex items-center gap-3">
          <Zap size={14} className="text-emerald-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-700">No assets generated yet</p>
            <p className="text-[11px] text-slate-500">Generate an asset pack using this angle to see it here.</p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Scenario Context Panel ─────────────────────────────────────────────── */

function ScenarioContextPanel({
  scenarioId,
  scenarioName,
  detail,
  loading,
}: {
  scenarioId: string;
  scenarioName?: string;
  detail: ScenarioDetail | null;
  loading: boolean;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-t border-slate-100">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-100/60 transition"
      >
        <div className="w-5 h-5 rounded-md bg-teal-100 flex items-center justify-center shrink-0">
          <BookOpen size={10} className="text-teal-600" />
        </div>
        <span className="text-[11px] font-bold text-teal-700 flex-1">
          Scenario Context
          {scenarioName && <span className="font-normal text-teal-600/80"> — {scenarioName}</span>}
        </span>
        {loading && <Loader2 size={10} className="animate-spin text-slate-400 shrink-0" />}
        <ChevronDown
          size={11}
          className={clsx('text-slate-400 transition-transform shrink-0', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="px-4 pb-4">
          {loading && !detail && (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 bg-slate-200 rounded w-full" />
              <div className="h-3 bg-slate-200 rounded w-4/5" />
              <div className="h-3 bg-slate-100 rounded w-2/3" />
            </div>
          )}

          {!loading && !detail && (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-1">
              <AlertTriangle size={12} />
              <span>Scenario details not available — <span className="font-mono text-[10px]">{scenarioId}</span></span>
            </div>
          )}

          {detail && (
            <div className="space-y-3">
              {/* Description */}
              {detail.description && (
                <p className="text-xs text-slate-700 leading-relaxed">{detail.description}</p>
              )}

              {/* Key signals row */}
              <div className="grid grid-cols-2 gap-2">
                {detail.purchase_intent && (
                  <ScenarioChip
                    icon={<TrendingUp size={9} />}
                    label="Purchase Intent"
                    value={detail.purchase_intent}
                    color="teal"
                  />
                )}
                {detail.decision_complexity && (
                  <ScenarioChip
                    icon={<Layers size={9} />}
                    label="Decision Complexity"
                    value={detail.decision_complexity}
                    color="violet"
                  />
                )}
                {detail.typical_timeline && (
                  <ScenarioChip
                    icon={<Clock size={9} />}
                    label="Typical Timeline"
                    value={detail.typical_timeline}
                    color="amber"
                  />
                )}
                {detail.scenario_type && (
                  <ScenarioChip
                    icon={<Target size={9} />}
                    label="Scenario Type"
                    value={detail.scenario_type.replace(/_/g, ' ')}
                    color="slate"
                  />
                )}
              </div>

              {/* Trigger event */}
              {detail.trigger_event && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Zap size={11} className="text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-0.5">Trigger Event</p>
                    <p className="text-xs text-slate-700">{detail.trigger_event}</p>
                  </div>
                </div>
              )}

              {/* Tags */}
              {detail.tags && detail.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detail.tags.slice(0, 6).map((tag) => (
                    <span
                      key={tag}
                      className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Confidence */}
              {detail.confidence_score != null && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-400 rounded-full"
                      style={{ width: `${Math.round(detail.confidence_score * 100)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0">
                    {Math.round(detail.confidence_score * 100)}% confidence
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScenarioChip({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'teal' | 'violet' | 'amber' | 'slate';
}) {
  const colorMap = {
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
    violet: 'bg-violet-50 border-violet-200 text-violet-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  return (
    <div className={clsx('flex flex-col gap-0.5 border rounded-lg px-2.5 py-2', colorMap[color])}>
      <div className="flex items-center gap-1 opacity-70">
        {icon}
        <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <span className="text-[11px] font-semibold capitalize">{value}</span>
    </div>
  );
}

/* ─── Generated Assets Panel ─────────────────────────────────────────────── */

function GeneratedAssetsPanel({
  completedPacks,
  activePacks,
  briefId,
  onNavigateToAssets,
}: {
  completedPacks: AdPackStatusResponse[];
  activePacks: AdPackStatusResponse[];
  briefId: string;
  onNavigateToAssets?: () => void;
}) {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  return (
    <div className="border-t border-slate-100">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-100/60 transition"
      >
        <div className="w-5 h-5 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
          <Package size={10} className="text-emerald-600" />
        </div>
        <span className="text-[11px] font-bold text-emerald-700 flex-1">
          Generated Assets
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {completedPacks.length > 0 && (
            <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">
              {completedPacks.length} completed
            </span>
          )}
          {activePacks.length > 0 && (
            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold flex items-center gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
              {activePacks.length} running
            </span>
          )}
        </div>
        <ChevronDown
          size={11}
          className={clsx('text-slate-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* In-progress packs */}
          {activePacks.map((job) => (
            <ActivePackRow
              key={job.job_id}
              job={job}
              briefId={briefId}
              onView={() =>
                navigate(`/creative/live/${encodeURIComponent(job.job_id)}?brief_id=${encodeURIComponent(briefId)}`)
              }
            />
          ))}

          {/* Completed packs */}
          {completedPacks.map((job) => (
            <CompletedPackRow
              key={job.job_id}
              job={job}
              briefId={briefId}
            />
          ))}

          {/* View all link */}
          <button
            onClick={onNavigateToAssets}
            className="flex items-center justify-center gap-1.5 w-full py-2 text-[11px] font-semibold text-teal-600 hover:text-teal-700 border border-dashed border-teal-200 hover:border-teal-300 rounded-lg transition"
          >
            View all in Assets tab <ChevronRight size={11} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Active Pack Row ────────────────────────────────────────────────────── */

function ActivePackRow({
  job,
  briefId,
  onView,
}: {
  job: AdPackStatusResponse;
  briefId: string;
  onView: () => void;
}) {
  const pct = Math.round(job.percent_complete ?? 0);

  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-xl px-3 py-2.5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Loader2 size={12} className="animate-spin text-amber-500 shrink-0" />
          <div>
            <p className="text-[11px] font-semibold text-slate-700">
              {job.pipeline_phase ? phaseLabelMap[job.pipeline_phase] ?? job.pipeline_phase : 'Generating…'}
            </p>
            <p className="text-[10px] text-slate-400 font-mono">{job.job_id.slice(0, 16)}…</p>
          </div>
        </div>
        <button
          onClick={onView}
          className="text-[10px] font-semibold text-teal-600 hover:text-teal-700 flex items-center gap-0.5"
        >
          Live view <ChevronRight size={10} />
        </button>
      </div>
      <div className="h-1 bg-amber-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 rounded-full transition-all"
          style={{ width: `${Math.max(pct, 3)}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-slate-400">
          {(job.phases_completed?.length ?? 0)} / {((job.phases_completed?.length ?? 0) + (job.phases_remaining?.length ?? 0))} phases
        </span>
        <span className="text-[10px] font-bold text-slate-500">{pct}%</span>
      </div>
    </div>
  );
}

const phaseLabelMap: Record<string, string> = {
  brief: 'Writing briefs',
  image: 'Generating images',
  review: 'Reviewing creatives',
  refine: 'Refining ads',
  package: 'Packaging',
  persona: 'Loading personas',
  concept: 'Building concepts',
};

/* ─── Completed Pack Row ─────────────────────────────────────────────────── */

function CompletedPackRow({
  job,
  briefId,
}: {
  job: AdPackStatusResponse;
  briefId: string;
}) {
  const navigate = useNavigate();
  const packId = job.output_pack_id || job.pack_id || null;
  const { thumbnails, loading: thumbLoading } = useThumbnails(packId, true);
  const [modalAsset, setModalAsset] = useState<ModalAsset | null>(null);

  const rankedAds = job.manifest?.ranked_ads ?? [];
  const displayName = (() => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const ad of rankedAds) {
      const p = ad.persona?.replace(/_/g, ' ');
      if (p && !seen.has(p)) { seen.add(p); names.push(p); }
    }
    if (names.length > 0) {
      const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
      return names.slice(0, 2).map(cap).join(' · ');
    }
    return packId ? packId.slice(0, 18) + '…' : 'Ad Pack';
  })();

  const packPath = packId
    ? `/creative/packs/${packId}${job.job_id ? `?job=${encodeURIComponent(job.job_id)}` : ''}`
    : null;

  const summary = job.manifest?.summary;
  const adsCount = job.ads_generated ?? summary?.total_ads ?? 0;
  const mesCES = summary?.mean_ces;

  const dateLabel = job.created_at
    ? new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <>
      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden hover:border-slate-300 transition">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-3 py-2.5">
          <div className="w-5 h-5 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
            <Check size={10} className="text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              onClick={() => packPath && navigate(packPath)}
              disabled={!packPath}
              className="text-[11px] font-semibold text-slate-800 hover:text-violet-700 transition truncate block text-left"
            >
              {displayName}
            </button>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                <Sparkles size={8} /> {adsCount} ads
              </span>
              {mesCES != null && (
                <span className="text-[10px] text-slate-400">
                  CES {Math.round(mesCES * 100)}
                </span>
              )}
              {dateLabel && <span className="text-[10px] text-slate-400">{dateLabel}</span>}
            </div>
          </div>
          {packPath && (
            <button
              onClick={() => navigate(packPath)}
              className="flex items-center gap-0.5 text-[10px] font-semibold text-teal-600 hover:text-teal-700 shrink-0"
            >
              View <ChevronRight size={10} />
            </button>
          )}
        </div>

        {/* Thumbnail strip */}
        <div className="px-3 pb-3">
          {thumbLoading && thumbnails.length === 0 && (
            <div className="grid grid-cols-4 gap-1.5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="aspect-square rounded-lg bg-slate-100 animate-pulse" />
              ))}
            </div>
          )}
          {!thumbLoading && thumbnails.length === 0 && packId && (
            <div className="grid grid-cols-4 gap-1.5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center"
                >
                  <ImageIcon size={10} className="text-slate-200" />
                </div>
              ))}
            </div>
          )}
          {thumbnails.length > 0 && (
            <ThumbnailStrip thumbnails={thumbnails} onThumbClick={setModalAsset} />
          )}
        </div>
      </div>

      {modalAsset && packId && (
        <AssetDetailModal
          asset={modalAsset}
          packId={packId}
          onClose={() => setModalAsset(null)}
          onNavigateToTemplate={(templateId) => {
            setModalAsset(null);
            navigate(`/creative-templates/${templateId}`);
          }}
        />
      )}
    </>
  );
}

/* ─── Thumbnail Strip ────────────────────────────────────────────────────── */

function ThumbnailStrip({
  thumbnails,
  onThumbClick,
}: {
  thumbnails: ModalAsset[];
  onThumbClick: (t: ModalAsset) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const preview = thumbnails.slice(0, 4);
  const extra = thumbnails.slice(4);

  return (
    <div>
      <div className="grid grid-cols-4 gap-1.5">
        {preview.map((thumb) => (
          <ThumbnailTile key={thumb.asset_id} thumb={thumb} onClick={() => onThumbClick(thumb)} />
        ))}
      </div>

      {extra.length > 0 && (
        <div
          className={clsx(
            'grid transition-all duration-300 ease-in-out mt-1.5',
            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          )}
        >
          <div className="overflow-hidden">
            <div className="grid grid-cols-4 gap-1.5">
              {extra.map((thumb) => (
                <ThumbnailTile key={thumb.asset_id} thumb={thumb} onClick={() => onThumbClick(thumb)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {thumbnails.length > 4 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-center gap-1 mt-1.5 w-full py-1 text-[10px] font-semibold text-slate-400 hover:text-teal-600 transition-colors"
        >
          {expanded ? 'Show less' : `+${extra.length} more`}
          <ChevronDown
            size={10}
            className={clsx('transition-transform duration-200', expanded && 'rotate-180')}
          />
        </button>
      )}
    </div>
  );
}

function ThumbnailTile({
  thumb,
  onClick,
}: {
  thumb: ModalAsset;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const url = thumb.thumbnail_url || thumb.media_url || null;
  const ces = thumb.scores?.['ces'] ?? null;

  return (
    <div
      className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {url ? (
        <img src={url} alt="Ad asset" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon size={14} className="text-slate-300" />
        </div>
      )}
      {ces !== null && !hovered && (
        <div className="absolute bottom-1 left-1 text-[8px] font-bold bg-black/50 text-white px-1 py-0.5 rounded-full backdrop-blur-sm">
          {Math.round(ces * 100)}
        </div>
      )}
      <div
        className={clsx(
          'absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-150',
          hovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <span className="text-[9px] font-semibold text-white bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
          View
        </span>
      </div>
    </div>
  );
}

/* ─── Detail Block ───────────────────────────────────────────────────────── */

function DetailBlock({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1 text-slate-500">
        {icon}
        <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xs text-slate-700 leading-relaxed">{children}</p>
    </div>
  );
}

/* ─── Rejected Collapse ──────────────────────────────────────────────────── */

function RejectedCollapse({
  angles,
  onRestore,
}: {
  angles: BriefAngle[];
  onRestore: (a: BriefAngle) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition"
      >
        <span className="w-2 h-2 rounded-full bg-slate-300" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Rejected
        </span>
        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">
          {angles.length}
        </span>
        <ChevronRight
          size={12}
          className={clsx('ml-auto text-slate-400 transition-transform', open && 'rotate-90')}
        />
      </button>
      {open && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {angles.map((a) => (
            <div
              key={a.angle_id}
              className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-slate-50"
            >
              <p className="flex-1 text-xs text-slate-400 line-through truncate">{a.hook}</p>
              <button
                onClick={() => onRestore(a)}
                className="text-[10px] font-semibold text-teal-600 hover:text-teal-700 shrink-0"
              >
                Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
